import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { CreateNewStaffState, StaffData, UpdatePassword, UserSelected } from "../../types/user";
import { defaultCreateNewStaff, initialUpdatePassword } from "../../initial-state/initial-state";
import { Default } from "../client/clientSlice";

interface UserState {
    selectedUser: StaffData | null;
    createNewStaff: CreateNewStaffState | null;
    typeOfOpen: 'role' | 'status' | 'name' | 'password' | null;
    updatePassword: UpdatePassword,
    selectedStaff: UserSelected | null;
}

const initialState: UserState  = {
    selectedUser: null,
    createNewStaff: defaultCreateNewStaff,
    typeOfOpen: null,
    updatePassword: initialUpdatePassword,
    selectedStaff: null,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setSelectedStaff: (state, action: PayloadAction<UserSelected | null>) => {
            state.selectedStaff = action.payload;
        },
        clearSelectedStaff: (state) => {
            state.selectedStaff = null;
        },

        setUpdatePassword: (state, action: PayloadAction<{ type: 'old' | 'new'; value: string }>) => {
            const { type, value } = action.payload;
            if (type === 'old') {
                state.updatePassword.oldPass = value;
            } else {
                state.updatePassword.newPass = value;
            }
        },
        clearUpdatePassword: (state) => {
            state.updatePassword = { oldPass: '', newPass: '' };
        },

        setTypeOfOpen: (state, action: PayloadAction<'role' | 'status' | 'name' | 'password' | null>) => {
            state.typeOfOpen = action.payload;
        },
        clearTypeOfOpen: (state) => {
            state.typeOfOpen = null;
        },

        setSelectedUser: (state, action: PayloadAction<StaffData | null>) => {
            state.selectedUser = action.payload;
        },
        clearSelectedUser: (state) => {
            state.selectedUser = null;
        },

        updateCreateNewStaffField: <T extends keyof CreateNewStaffState>(
            state: UserState,
            action: PayloadAction<{ field: T; value: CreateNewStaffState[T] }>
        ) => {
            const { field, value } = action.payload;

            if (state.createNewStaff) {
                state.createNewStaff[field] = value;
            }
        },

        resetCreateNewStaff: (state) => {
            state.createNewStaff = defaultCreateNewStaff;
        },
    },
});

export const { 
    setSelectedUser, 
    clearSelectedUser,
    updateCreateNewStaffField,
    resetCreateNewStaff,

    setTypeOfOpen,
    clearTypeOfOpen,
    setUpdatePassword,
    clearUpdatePassword,
    setSelectedStaff,
    clearSelectedStaff,
} = userSlice.actions;
export default userSlice.reducer;

export const selectCurrentUserData = (state: RootState) => state.user.selectedUser;
export const selectCurrentStaffId = (state: RootState) => state.user.selectedUser?.staffID;
export const selectCreateNewStaff = (state: RootState) => state.user.createNewStaff;
export const selectTypeOfOpen = (state: RootState) => state.user.typeOfOpen;
export const selectUpdatePassword = (state: RootState) => state.user.updatePassword;
export const selectSelectedStaff = (state: RootState) => state.user.selectedStaff;
export const selectSelectedStaffId = (state: RootState) => state.user.selectedStaff?.id;